import { CheckCircleFilled, CloseOutlined, MinusCircleFilled } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useEventListener } from 'ahooks';
import {
  Table,
  Alert,
  Button,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  ModalProps,
  Badge,
  Popover,
  Popconfirm,
  // Progress,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import chatttSDK from 'services/chattt';

const BlueText = styled.span`
  color: #336fff;
  margin: 0 0.3em;
`;

export interface MultiAuthInterruptModalProps
  extends Omit<
    ModalProps,
    'footer' | 'title' | 'closable' | 'maskClosable' | 'onCancel' | 'closeIcon'
  > {
  onConfirm?: (continueUpload?: boolean) => void;
}

const getFinishStatus = (data: RPA.Shopee.AUTH_AGENT_UPDATE_PAYLOAD['subTasks'][number]) => {
  if (data.status === 'DoneWithFailedAll') {
    return 'FailedAll';
  }
  if (data.status === 'DoneWithAbort') {
    return 'AbortBeforeRun';
  }
  if (data.status === 'DoneWithFinishParticial' || data.status === 'DoneWithFinishAll') {
    const result = data.authResult?.CHAT || data.authResult?.ERP;
    if (data.status === 'DoneWithFinishParticial' || result?.nonMatchShops?.length) {
      return 'PartialSuccess';
    }
    return 'AllSuccess';
  }
  return 'NotFinish';
};
const MultiAuthInterruptModal = (props: MultiAuthInterruptModalProps) => {
  const { visible, onConfirm, ...modalProps } = props;
  const [authData, setAuthData] = useState<RPA.Shopee.AUTH_AGENT_UPDATE_PAYLOAD | null>(null);
  const [otp, setOtp] = useState('');
  const { t } = useTranslate();
  const [isCanceled, setIsCanceled] = useState(false);
  useEffect(() => {
    if (visible) {
      setIsCanceled(false);
    }
  }, [visible]);
  useEventListener(
    'message',
    (e: MessageEvent<RPA.Shopee.POSTMESSAGE_DATA<RPA.Shopee.AUTH_AGENT_UPDATE_PAYLOAD>>) => {
      if (!visible) {
        return;
      }
      if (e?.data?.event === 'CHATTT_AUTH_AGENT_UPDATE' && e?.data?.payload) {
        const {
          data: { payload },
        } = e;
        setAuthData(payload || null);
      }
    },
  );
  const { content, title, footer, isInterrupt, closeIcon } = useMemo(() => {
    const cacnelTask = () => {
      if (chatttSDK?.shopee?.multiAuth) {
        if (authData?.taskId) {
          const { taskId } = authData;
          chatttSDK?.shopee?.multiAuth('cancel', {
            taskId,
          });
        }
        setIsCanceled(true);
      }
    };
    let finishCount = 0;
    let restCount = 0;
    let title: React.ReactNode = null;
    let isInterrupt = false;
    let closeIcon = (
      <Popconfirm
        onConfirm={cacnelTask}
        title={t('ViewShops.shopee.multi.interrupt.modal.cancel.task.confirm.tips')}
      >
        <CloseOutlined />
      </Popconfirm>
    );
    let content: React.ReactNode = (
      <Row justify="center">
        <Col>
          <Spin
            spinning
            tip={t(
              isCanceled
                ? 'ViewShops.shopee.multi.interrupt.modal.cancelling'
                : 'ViewShops.shopee.multi.interrupt.modal.progressing',
            )}
          />
        </Col>
      </Row>
    );
    let footer: React.ReactNode = null;
    if (visible && authData !== null) {
      const { subTasks, taskId } = authData;
      let needInterruptTask: RPA.Shopee.AuthAgentJSON | null = null;
      const isTaskNeedInterrupt = (task: RPA.Shopee.AuthAgentJSON) => /^WaitFor/.test(task.status);
      subTasks.forEach((t) => {
        const { status } = t;
        if (isTaskNeedInterrupt(t)) {
          if (!needInterruptTask) {
            needInterruptTask = t;
          }
        } else if (status.startsWith('Done')) {
          finishCount += 1;
        }
      });
      restCount = subTasks.length - finishCount;
      const progressBar = (
        <div
          className={css`
            display: flex;
            justify-content: center;
            margin: -10px 0 15px 0;
          `}
        >
          <Space size="large">
            <div>
              <CheckCircleFilled
                className={css`
                  color: #52c01a;
                  margin-right: 8px;
                `}
              />
              {t('ViewShops.shopee.multi.interrupt.modal.tasks.finish.count.tips.prev')}
              <BlueText>{finishCount}</BlueText>
              {t('ViewShops.shopee.multi.interrupt.modal.tasks.count.tips.unit')}
            </div>
            <div>
              <MinusCircleFilled
                className={css`
                  color: #ccc;
                  margin-right: 8px;
                `}
              />
              {t('ViewShops.shopee.multi.interrupt.modal.tasks.rest.count.tips.prev')}
              <BlueText>{restCount}</BlueText>
              {t('ViewShops.shopee.multi.interrupt.modal.tasks.count.tips.unit')}
            </div>
          </Space>
        </div>
      );

      if (needInterruptTask) {
        isInterrupt = true;
        const {
          status,
          id: needInterruptTaskId,
          info: { account },
        } = needInterruptTask as RPA.Shopee.AuthAgentJSON;
        title = t('ViewShops.shopee.multi.interrupt.modal.manual.title');
        const skipInterrupt = () => {
          if (chatttSDK?.shopee?.multiAuth) {
            chatttSDK?.shopee?.multiAuth('skip:interrupt', {
              taskId,
              subTaskId: needInterruptTaskId,
            });
          }
          setOtp('');
        };
        content = (
          <div>
            {progressBar}
            <Row
              justify="start"
              align="middle"
              className={css`
                margin: 8px auto;
              `}
            >
              <Col span={3}>
                {t('ViewShops.shopee.multi.interrupt.modal.tasks.labels.account')}:
              </Col>
              <Col span={18}>
                <Input value={account} disabled />
              </Col>
            </Row>
            {(status === 'WaitForOTP' || status === 'WaitForOTPRetry') && (
              <>
                <Row
                  justify="start"
                  align="middle"
                  className={css`
                    margin: 8px auto 2.8em auto;
                  `}
                >
                  <Col span={3}>
                    {t('ViewShops.shopee.multi.interrupt.modal.tasks.labels.otp')}:
                  </Col>
                  <Col
                    className={css`
                      position: relative;
                    `}
                    span={18}
                  >
                    <Input
                      value={otp}
                      className={css`
                        border-color: ${status === 'WaitForOTPRetry' ? 'red' : ''};
                      `}
                      onChange={(e) => {
                        setOtp(e.target.value);
                      }}
                    />
                    {status === 'WaitForOTPRetry' && (
                      <div
                        className={css`
                          position: absolute;
                          left: 0;
                          bottom: -1.8em;
                          color: red;
                        `}
                      >
                        {t('ViewShops.shopee.multi.interrupt.modal.tasks.errors.otp')}
                      </div>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col span={3} />
                  <Col span={18}>
                    <Button
                      className={css`
                        width: 100%;
                      `}
                      type="primary"
                      disabled={!otp}
                      onClick={() => {
                        if (chatttSDK?.shopee?.multiAuth) {
                          chatttSDK.shopee.multiAuth('continue:otp', {
                            taskId,
                            subTaskId: needInterruptTaskId,
                            otp,
                          });
                        }
                        setOtp('');
                      }}
                    >
                      {t('ViewShops.shopee.multi.interrupt.modal.tasks.buttons.auth.confirm')}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            {status === 'WaitForImageCaptcha' && (
              <>
                <Row
                  justify="start"
                  align="middle"
                  className={css`
                    margin: 8px auto;
                  `}
                >
                  <Col span={3} />
                  <Col span={18}>
                    <Alert
                      type="warning"
                      showIcon
                      message={t(
                        'ViewShops.shopee.multi.interrupt.modal.tasks.manual.image.captcha.tips',
                      )}
                    />
                  </Col>
                </Row>
                <Row
                  justify="start"
                  align="middle"
                  className={css`
                    margin: 8px auto;
                  `}
                >
                  <Col span={3} />
                  <Col span={18}>
                    <Button
                      className={css`
                        width: 100%;
                      `}
                      type="primary"
                      onClick={() => {
                        if (chatttSDK?.shopee?.multiAuth) {
                          chatttSDK.shopee.multiAuth('continue:imageCaptcha', {
                            taskId,
                            subTaskId: needInterruptTaskId,
                          });
                        }
                      }}
                    >
                      {t('ViewShops.shopee.multi.interrupt.modal.tasks.buttons.go.validate')}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            <Row
              justify="start"
              align="middle"
              className={css`
                margin: 8px auto;
              `}
            >
              <Col span={3} />
              <Col span={18}>
                <Button
                  className={css`
                    width: 100%;
                  `}
                  type="link"
                  onClick={skipInterrupt}
                >
                  {t('ViewShops.shopee.multi.interrupt.modal.tasks.buttons.skip')}
                </Button>
              </Col>
            </Row>
          </div>
        );
      } else if (restCount <= 0) {
        title = t('ViewShops.shopee.multi.interrupt.modal.result.title');
        footer = (
          <Row justify="end">
            <Col>
              <Button
                onClick={(_e) => {
                  onConfirm && onConfirm(true);
                }}
                type="link"
              >
                {t('ViewShops.shopee.multi.interrupt.modal.tasks.buttons.upload.again')}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={(_e) => {
                  onConfirm && onConfirm();
                }}
              >
                {t('ViewShops.shopee.multi.interrupt.modal.tasks.buttons.auth.end')}
              </Button>
            </Col>
          </Row>
        );
        content = (
          <>
            <div>{progressBar}</div>
            <Table
              dataSource={subTasks}
              columns={[
                {
                  title: t('ViewShops.shopee.multi.interrupt.modal.result.col.account'),
                  dataIndex: 'info',
                  key: 'info.account',
                  render: (_, record) => {
                    return record?.info?.account || '-';
                  },
                },
                {
                  title: t('ViewShops.shopee.multi.interrupt.modal.result.col.stores'),
                  dataIndex: 'info.shopNames',
                  render: (_, record) => {
                    const result = record.authResult?.CHAT || record.authResult?.ERP;
                    if (result?.authedShops) {
                      const hide = result?.authedShops.map((shop) => <span>{shop.name}</span>);
                      if (result.authedShops.length <= 3) {
                        return <Space size="middle">{hide}</Space>;
                      }
                      const slice3 = hide.splice(0, 3);
                      return (
                        <>
                          <Space size="middle">{slice3}</Space>
                          {hide.length > 0 && (
                            <Row justify="end">
                              <Col>
                                <Popover content={<Space size="middle">{hide}</Space>}>
                                  <Button type="link">还有{hide.length}个</Button>
                                </Popover>
                              </Col>
                            </Row>
                          )}
                        </>
                      );
                    }
                    return '';
                  },
                },
                {
                  title: t('ViewShops.shopee.multi.interrupt.modal.result.col.result'),
                  dataIndex: 'status',
                  render: (_, record) => {
                    const result = getFinishStatus(record);
                    switch (result) {
                      case 'AllSuccess': {
                        return (
                          <>
                            <Badge status="success" />
                            {t('ViewShops.shopee.multi.interrupt.modal.result.status.all.finish')}
                          </>
                        );
                      }
                      case 'PartialSuccess': {
                        return (
                          <>
                            <Badge status="success" />
                            {t(
                              'ViewShops.shopee.multi.interrupt.modal.result.status.partial.finish',
                            )}
                          </>
                        );
                      }
                      case 'FailedAll': {
                        return (
                          <>
                            <Badge status="error" />
                            {t('ViewShops.shopee.multi.interrupt.modal.result.status.all.failed')}
                          </>
                        );
                      }
                      case 'AbortBeforeRun': {
                        return (
                          <>
                            <Badge status="error" />
                            {t('ViewShops.shopee.multi.interrupt.modal.result.status.abort')}
                          </>
                        );
                      }
                      default: {
                        break;
                      }
                    }
                    return '-';
                  },
                },
                {
                  title: t('ViewShops.shopee.multi.interrupt.modal.result.col.desc'),
                  key: 'desc',
                  render: (_, record) => {
                    if (record?.status === 'DoneWithAbort') {
                      return t('ViewShops.shopee.multi.interrupt.modal.result.desc.abort');
                    }
                    const result = record.authResult?.CHAT || record.authResult?.ERP;
                    if (!result) {
                      return t('ViewShops.shopee.multi.interrupt.modal.result.desc.default.error');
                    }
                    if (record.status === 'DoneWithFinishAll') {
                      const total =
                        result.authedShops?.length ||
                        t('ViewShops.shopee.multi.interrupt.modal.result.desc.all.finish.default');
                      return (
                        <>
                          {t('ViewShops.shopee.multi.interrupt.modal.result.desc.all.finish.prev')}
                          <BlueText>{total}</BlueText>
                          {t('ViewShops.shopee.multi.interrupt.modal.result.desc.all.finish.unit')}
                        </>
                      );
                    }

                    const { error: detailErrorKey, postErrorCode } = result;
                    const targeErrors = ['1005', '1012', '1013'] as const;
                    if (postErrorCode && ['1005', '1012', '1013'].includes(`${postErrorCode}`)) {
                      const errorCode = postErrorCode as unknown as (typeof targeErrors)[number];
                      const i18nkey = `shopee.auth.error.tips.of.codes.${errorCode}`;
                      return t(`ViewShops.${i18nkey}`);
                    }
                    return t({
                      key: `ViewShops.${detailErrorKey}`,
                      defaultValue: t('ViewShops.shopee.multi.auth.errors.fallback'),
                    });
                  },
                },
                {
                  title: t('操作'),
                  key: 'actions',
                  render: (_, record) => {
                    const result = getFinishStatus(record);
                    switch (result) {
                      case 'AllSuccess':
                      case 'PartialSuccess': {
                        const detailResult = record.authResult?.ERP || record?.authResult?.CHAT;
                        if (detailResult) {
                          const { nonMatchShops } = detailResult;
                          return (
                            <Button
                              onClick={() => {
                                Modal.info({
                                  width: '60%',
                                  icon: null,
                                  title: `${record.info.account}-${t(
                                    'ViewShops.shopee.multi.interrupt.detail.modal.title',
                                  )}`,
                                  content: (
                                    <div>
                                      <Table
                                        rowKey="name"
                                        dataSource={detailResult.allShops}
                                        columns={[
                                          {
                                            title: t(
                                              'ViewShops.shopee.multi.interrupt.detail.modal.col.shopname',
                                            ),
                                            dataIndex: 'name',
                                          },
                                          {
                                            title: t(
                                              'ViewShops.shopee.multi.interrupt.detail.modal.col.result',
                                            ),
                                            dataIndex: 'name',
                                            render: (name, _record) => {
                                              if (
                                                nonMatchShops &&
                                                nonMatchShops.some((t) => t.name === name)
                                              ) {
                                                return (
                                                  <>
                                                    <Badge status="error" />
                                                    {t(
                                                      'ViewShops.shopee.multi.interrupt.detail.modal.status.failed',
                                                    )}
                                                  </>
                                                );
                                              }
                                              return (
                                                <>
                                                  <Badge status="success" />
                                                  {t(
                                                    'ViewShops.shopee.multi.interrupt.detail.modal.status.success',
                                                  )}
                                                </>
                                              );
                                            },
                                          },
                                          {
                                            title: t(
                                              'ViewShops.shopee.multi.interrupt.detail.modal.col.desc',
                                            ),
                                            dataIndex: 'name',
                                            render: (name, _record) => {
                                              if (
                                                nonMatchShops &&
                                                nonMatchShops.some((t) => t.name === name)
                                              ) {
                                                return t(
                                                  'ViewShops.shopee.multi.interrupt.detail.modal.desc.txt.shop.not.found',
                                                );
                                              }
                                              return t(
                                                'ViewShops.shopee.multi.interrupt.detail.modal.desc.txt.success',
                                              );
                                            },
                                          },
                                        ]}
                                      />
                                    </div>
                                  ),
                                });
                              }}
                              type="link"
                            >
                              {t('tableColumn.查看详情')}
                            </Button>
                          );
                        }
                        break;
                      }
                      default: {
                        break;
                      }
                    }

                    return '-';
                  },
                },
              ]}
            />
          </>
        );
        closeIcon = <CloseOutlined onClick={() => onConfirm && onConfirm()} />;
      }
      // else {
      //   const percentage = finishCount / subTasks.length;
      //   content = (
      //     <Row justify="center">
      //       <Col>
      //         <Spin spinning tip="批量执行中..." />
      //       </Col>
      //     </Row>
      //   );
      // }
    }
    return { content, title, footer, isInterrupt, closeIcon };
  }, [t, isCanceled, visible, authData, otp, onConfirm]);
  return (
    <>
      <Modal
        closeIcon={closeIcon}
        width={!isInterrupt ? '70%' : undefined}
        title={title}
        footer={footer}
        // onCancel={onCancel}
        // closable={closable}
        maskClosable={false}
        visible={visible}
        {...modalProps}
      >
        <div>{content}</div>
      </Modal>
    </>
  );
};
export default MultiAuthInterruptModal;
